
import BagFinderService from '@/services/Three/bag-finder.service'
import { useStore } from '@/store'
import { ThreeActionTypes } from '@/store/modules/three/action-types'
import * as THREE from 'three'
import { GLTF, GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'
import { defineComponent } from 'vue'
import { computed, watch } from 'vue'
import RandselMetal from '@/components/Three/RandselMetal.vue'
import RandselTransparent from '@/components/Three/RandselTransparent.vue'
import ThreeRandselBody from '@/components/Three/options/ThreeRandselBody.vue'
import ThreeRandselEdge from '@/components/Three/options/ThreeRandselEdge.vue'
import ThreeRandselThread from '@/components/Three/options/ThreeRandselThread.vue'
import ThreeRandselInside from '@/components/Three/options/ThreeRandselInside.vue'
import ThreeRandselFront from '@/components/Three/options/ThreeRandselFront.vue'
import ThreeRandselSide from '@/components/Three/options/ThreeRandselSide.vue'
import ThreeRandselPocket from '@/components/Three/options/ThreeRandselPocket.vue'
import ThreeRandselTack from '@/components/Three/options/ThreeRandselTack.vue'
import ThreeRandselBack from '@/components/Three/options/ThreeRandselBack.vue'
import ThreeRandselName from '@/components/Three/options/ThreeRandselName.vue'
import References from '@/singletons/references'
import TextureLoadService from '@/services/Three/texture-load.service'
import Analytics from '@/singletons/analytics'
import ZipLoader from '@/utils/ZipLoader'
import SentrySingleton from '@/singletons/sentry-singleton'

export default defineComponent({
  name: 'ThreeRandselBag',
  components: {
    RandselMetal,
    RandselTransparent,
    ThreeRandselBody,
    ThreeRandselEdge,
    ThreeRandselThread,
    ThreeRandselInside,
    ThreeRandselPocket,
    ThreeRandselSide,
    ThreeRandselFront,
    ThreeRandselTack,
    ThreeRandselBack,
    ThreeRandselName
  },
  setup() {
    const store = useStore()
    const isSceneLoaded = computed(() => store.getters.isSceneLoaded)

    // prettier-ignore
    watch(() => isSceneLoaded.value, (_) => {
      load()
      setTimeout(() => {
        loadCommonTextures()
      }, 100)
    })

    async function load() {
      const d = new Date()
      console.log(d.toLocaleTimeString() + `.${d.getMilliseconds()}` + ': load bag...')

      try {
        const manager = new THREE.LoadingManager()
        const MODEL_VERSION = '2025-12-28_1'
        const url = `lnxc5i9p4k/sbnclgdshm.glb.zip?v=${MODEL_VERSION}`

        await waitToLoadBag()

        const loadArrayBuffer = async (u: string) => {
          const res = await fetch(u)
          if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`)
          return await res.arrayBuffer()
        }

        const loadGlbFromZip = async (zipUrl: string) => {
          const zipBytes = await loadArrayBuffer(zipUrl)
          const { default: JSZip } = await import('jszip')
          const zip = await JSZip.loadAsync(zipBytes)

          const fileName =
            Object.keys(zip.files).find((n) => /\.(glb)$/i.test(n) && !zip.files[n].dir) ||
            Object.keys(zip.files).find((n) => /\.(gltf)$/i.test(n) && !zip.files[n].dir)

          if (!fileName) throw new Error('No .glb/.gltf found inside zip')

          if (/\.glb$/i.test(fileName)) {
            return await zip.file(fileName)!.async('arraybuffer')
          }

          const gltfText = await zip.file(fileName)!.async('string')
          const encoder = new TextEncoder()
          return encoder.encode(gltfText).buffer
        }

        const cleanUrl = url.split('?')[0]
        const isZip = cleanUrl.toLowerCase().endsWith('.zip')
        const data = isZip ? await loadGlbFromZip(url) : await loadArrayBuffer(url)

        const magic = new TextDecoder().decode(new Uint8Array(data, 0, 4))
        if (magic !== 'glTF' && magic.trim()[0] !== '{') {
          throw new Error(`Unexpected header: "${magic}"`)
        }

        const resourcePath = THREE.LoaderUtils.extractUrlBase(url.replace(/\.zip$/i, ''))
        const gltfLoader = new GLTFLoader(manager)

        gltfLoader.parse(data, resourcePath, onLoad, onError)
      } catch (err) {
        SentrySingleton.instance.captureException(err)
        onError(err)
      }
    }

    // function load() {
    //   const d = new Date()
    //   console.log(d.toLocaleTimeString() + `.${d.getMilliseconds()}` + ': load bag...')
    //   try {
    //     const manager = new THREE.LoadingManager()
    //     const url = 'lnxc5i9p4k/sbnclgdshm.glb.zip'
    //
    //     waitToLoadBag()
    //       .then(() => loadZip(url, manager))
    //       .then((file: any) => {
    //         new GLTFLoader(manager).load(file, onLoad, undefined, onError)
    //       })
    //   } catch (err) {
    //     SentrySingleton.instance.captureException(err)
    //   }
    // }

    async function loadZip(url: string, manager: THREE.LoadingManager) {
      return new Promise((resolve) => {
        if (url.match(/\.zip$/)) {
          ZipLoader.load(url, undefined, onError).then(function(zip: any) {
            manager.setURLModifier(zip.urlResolver)
            resolve(zip.find(/\.(gltf|glb)$/i)[0])
          })
        } else {
          resolve(url)
        }
      })
    }

    async function waitToLoadBag() {
      // wait just in case...
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve('')
        }, 200)
      })
    }

    function onLoad(gltf: GLTF) {
      const d = new Date()
      console.log(d.toLocaleTimeString() + `.${d.getMilliseconds()}` + ': bag loaded')
      try {
        const bagModel = new BagFinderService().find(gltf)
        bagModel.parent.add(bagModel.bag!)
        bagModel.bag.position.set(0, -1, 0.159)
        References.instance.getSceneReferences().scene.add(bagModel.parent)
        disableFrustumCulled(bagModel.bag)
        References.instance.setBagReferences(bagModel)
        hideLogo(bagModel.bag)
        store.dispatch(ThreeActionTypes.SET_BAG_LOADED, true)
      } catch (err) {
        SentrySingleton.instance.captureException(err)
      }
    }

    function hideLogo(bag: THREE.Group) {
      const logo = bag.getObjectByName('logo')
      if (logo) {
        logo.visible = false
      }
    }

    function disableFrustumCulled(model: THREE.Group) {
      model.traverse((o: any) => {
        if (o.isMesh) {
          o.frustumCulled = false
        }
      })
    }

    function onError(error: any) {
      console.log(error)
      Analytics.instance.sendModelLoadErrorEvent('Cannot load bag model')
      SentrySingleton.instance.captureException(error)
      alert('データが正しくロードされませんでした。\nOKを押すとページを再読み込みします。')
      // location.reload()
    }

    function loadCommonTextures() {
      try {
        const textureLoadService = new TextureLoadService(References.instance.getSceneReferences().maxAnisotropy)
        textureLoadService.loadImage('default/default_n', (tex: THREE.Texture | null) => {
          References.instance.setTexDefaultN(tex!)
          store.dispatch(ThreeActionTypes.SET_TEX_DEFAULT_N, tex!.uuid)

          textureLoadService.loadImage('default/body_n', (tex: THREE.Texture | null) => {
            References.instance.setTexBodyN(tex!)
            store.dispatch(ThreeActionTypes.SET_TEX_BODY_N, tex!.uuid)
          })
        })
        textureLoadService.loadImage('default/body', (tex: THREE.Texture | null) => {
          References.instance.setTexBody(tex!)
          store.dispatch(ThreeActionTypes.SET_TEX_BODY, tex!.uuid)
        })
      } catch (err) {
        SentrySingleton.instance.captureException(err)
      }
    }

    return {
      isSceneLoaded
    }
  }
})
